import { useCallback, useMemo, useState } from "react";
import { deleteFromDB, modalHandler, removeItemById, success } from "@";
import { useQueryClient } from "@tanstack/react-query";
import { useCanShow } from "@/shared/hooks";
import { useDriversQuery } from "../api/queries";
import { useSaveDriverMutation } from "../api/mutations";
import { driversKeys } from "../api/queries/keys";
import { createTableColumns } from "../components/TableOptions";
import type { Driver, DriverFormValues, DriversListResponse } from "../types";
import type { ColumnDef } from "@tanstack/react-table";

interface PaginationState {
  page: number;
  size: number;
}

interface UseDriversPageReturn {
  drivers: Driver[];
  columns: ColumnDef<Driver>[];
  isLoading: boolean;
  error: Error | null;
  totalItems: number;
  pg: PaginationState;
  updatePg: (newPg: Partial<PaginationState>) => void;
  refetch: () => void;
  saveDriver: (data: DriverFormValues) => Promise<void>;
  deleteDriver: (id: number) => Promise<boolean>;
  isSaving: boolean;
  editingDriver: Driver | null;
  openEditModal: (driver: Driver) => void;
  closeModal: () => void;
  searchName: string;
  updateSearchName: (value: string) => void;
}

export const useDriversPage = (): UseDriversPageReturn => {
  const queryClient = useQueryClient();
  const canEdit = useCanShow("canDoProjectVerification");

  const [pg, setPg] = useState<PaginationState>({ page: 1, size: 10 });
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [searchName, setSearchName] = useState("");

  const { data: queryData, isLoading, error, refetch } = useDriversQuery();
  const allDrivers = queryData?.drivers || [];

  const filtered = useMemo(() => {
    const q = searchName.trim().toLowerCase();
    if (!q) return allDrivers;
    return allDrivers.filter((d) => d.name?.toLowerCase().includes(q));
  }, [allDrivers, searchName]);

  const totalItems = filtered.length;

  const drivers = useMemo(() => {
    const start = (pg.page - 1) * pg.size;
    return filtered.slice(start, start + pg.size);
  }, [filtered, pg.page, pg.size]);

  const saveDriverMutation = useSaveDriverMutation();

  const updatePg = useCallback((newPg: Partial<PaginationState>) => {
    setPg((prev) => ({ ...prev, ...newPg }));
  }, []);

  const updateSearchName = useCallback((value: string) => {
    setSearchName(value);
    setPg((prev) => ({ ...prev, page: 1 }));
  }, []);

  const saveDriver = useCallback(
    async (data: DriverFormValues) => {
      await saveDriverMutation.mutateAsync({
        id: data.id ?? undefined,
        name: data.name,
        phoneNumber: data.phoneNumber || undefined,
        password: data.password || undefined,
        permissions: data.permissions,
      });
      success("تم الحفظ بنجاح");
    },
    [saveDriverMutation],
  );

  const deleteDriver = useCallback(
    async (id: number): Promise<boolean> => {
      const res = await deleteFromDB(id, "drivers");
      if (res) {
        queryClient.setQueryData<DriversListResponse>(
          driversKeys.list(),
          (oldData) => {
            if (!oldData?.drivers) return oldData;
            return {
              ...oldData,
              drivers: removeItemById(oldData.drivers, id),
            };
          },
        );
      }
      return Boolean(res);
    },
    [queryClient],
  );

  const handleDelete = useCallback(
    async (id: number) => {
      const ok = await deleteDriver(id);
      if (ok) success("تم الحذف بنجاح");
    },
    [deleteDriver],
  );

  const openEditModal = useCallback((driver: Driver) => {
    setEditingDriver(driver);
    modalHandler();
  }, []);

  const closeModal = useCallback(() => {
    setEditingDriver(null);
  }, []);

  const columns = useMemo(
    () =>
      createTableColumns({
        canEdit,
        onEdit: openEditModal,
        onDelete: handleDelete,
      }),
    [canEdit, openEditModal, handleDelete],
  );

  return {
    drivers,
    columns,
    isLoading,
    error: error as Error | null,
    totalItems,
    pg,
    updatePg,
    refetch,
    saveDriver,
    deleteDriver,
    isSaving: saveDriverMutation.isPending,
    editingDriver,
    openEditModal,
    closeModal,
    searchName,
    updateSearchName,
  };
};
